<template>
    <div>
        <portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex w-full justify-end overflow-hidden items-center">
                <Select :placeholder="i18n.t('general.sortBy')" v-model="sortBy" size="small" class="select-type">
                    <Option
                        v-for="option in sortOptions"
                        :key="option.prop === 'callDuration' ? option.label : `${option.label} - ${option.order}`"
                        :value="option"
                        :label="getSortingLabel(option)"
                    />
                </Select>
            </div>
        </portal>
        <portal :to="`widget-header__${data.WidgetID}-action`">
            <div class="cursor-pointer hidden lg:block">
                <template >
                    <Dropdown
                        size="mini"
                        class="px-2 py-1-5 hover:bg-primary-100 rounded"
                        trigger="click"
                        :hide-on-click="false"
                    >
                        <span class="flex items-center">
                            <i class="vc-icon-filter text-xl text-primary" />
                        </span>
                        <DropdownMenu slot="dropdown" >
                            <DropdownItem>
                                <div class="rounded mt-1 flex flex-col p-2">
                                    <BaseRadioGroup
                                        v-model="columnsMode"
                                        :radios="[
                                            {
                                                label: $t('settings.extension.columnsMode.static'),
                                                value: REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC
                                            },
                                            {
                                                label: $t('settings.extension.columnsMode.auto'),
                                                value: REAL_TIME_WIDGET_CARDS_COLUMN_MODE.AUTO
                                            }
                                        ]"
                                        class="radio-groups mb-2"
                                    />

                                    <template v-if="columnsMode === REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC">
                                        <div class="main-bg-light rounded-t border-b-2">
                                            <p class="px-2 text-main-sm font-medium main-text-color">
                                              {{ layoutColumns + ' '}}{{ $t('columns.layout') }}
                                            </p>
                                        </div>
                                        <div class="w-full flex p-2 justify-between main-bg-light">
                                            <div
                                                v-for="index in 15"
                                                :key="index"
                                                @click="updateGrid(index)"
                                                :class="{'bg-primary-100': index <= layoutColumns}"
                                                class="square mx-margin--1"
                                            >
                                            </div>
                                        </div>
                                    </template>

                                    <template v-else>
                                        <Select
                                            v-model="columnsSize"
                                            :placeholder="$t('settings.extension.autoColumns.size')"
                                            size="small"
                                            style="min-width: 320px;"
                                        >
                                            <Option
                                                v-for="size in REAL_TIME_WIDGET_CARDS_COLUMN_SIZE"
                                                :key="size"
                                                :value="size"
                                                :label="$t(`settings.extension.autoColumns.size.${size}`)"
                                            />
                                        </Select>
                                    </template>

                                </div>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </template>
            </div>
        </portal>
        <div class="flex justify-center">
            <fade-transition>
                <div
                    class="w-full redesign-extension-cards-wrap overflow-x-auto"
                    :class="{ 'columns-auto': !isStaticColumnsMode }"
                >
                    <template v-if="sortedExtensionsDebounced.length">
                        <div
                            class="redesign-extension-cards p-4 gap-4"
                            :style="renderColumns"
                            ref="parent"
                        >
                            <redesign-base-card
                                v-for="(extension) in sortedExtensions"
                                :key="extension[extensionIdProp]"
                                :shadow="shadowColors[extension[extensionIdProp]]"
                                :class="cardWrapClasses"
                                class="redesign-base-card-container"
                            >
                                <template v-slot:content>
                                    <redesign-extension-card class="redesign-extension-card-container" :extension="extension">
                                        <template v-slot:header>
                                            <redesign-extension-card-header
                                                :settings="widgetSettings"
                                                :cards-cols="layoutColumnsBasedOnColumnsMode"
                                                :show-summary-row="!!showBodyAllCalls[extension[extensionIdProp]]"
                                                @toggleSummary="(showSummary) => setSummaryVisibility(extension[extensionIdProp], showSummary)"
                                                @statusColorChanged="(color) => changeShadowColor(color, extension[extensionIdProp])"
                                            />
                                        </template>

                                        <template v-slot:body>
                                            <redesign-extension-card-body
                                                :cards-cols="layoutColumnsBasedOnColumnsMode"
                                                :extension="extension"
                                                :settings="widgetSettings"
                                                :show-summary="!!summaryOpened[extension[extensionIdProp]]"
                                                @toggle="(val) => showAllCalls(extension[extensionIdProp], val)"
                                                :show-all-calls="!!showBodyAllCalls[extension[extensionIdProp]]"
                                            />
                                        </template>
                                    </redesign-extension-card>
                                </template>
                            </redesign-base-card>
                        </div>
                    </template>
                </div>
            </fade-transition>
        </div>
    </div>
</template>
<script setup>
import {get, set, partition} from 'lodash'
import { ref, computed, watch, defineEmits } from 'vue';
import { useThrottle } from '@vueuse/core'
import store from "@/store/store";
import times from 'lodash/times'
import i18n from "@/i18n";
import RedesignBaseCard from "@/components/RedesignBaseComponents/RedesignBaseCard.vue";
import RedesignExtensionCard from "@/components/RedesignComponents/Cards/RedesignExtensionCard.vue";
import RedesignExtensionCardHeader from "@/components/RedesignComponents/Cards/ExtensionCard/RedesignExtensionCardHeader.vue";
import RedesignExtensionCardBody from "@/components/RedesignComponents/Cards/ExtensionCard/RedesignExtensionCardBody.vue";
import { Option, Select, DropdownMenu, Dropdown, DropdownItem } from 'element-ui'
import {
    REAL_TIME_WIDGET_CARDS_COLUMN_MODE,
    REAL_TIME_WIDGET_CARDS_COLUMN_SIZE,
    realTimeSettings
} from '@/enum/defaultWidgetSettings'
import { getWidgetData } from "@/services/widgetService";
import {useEntitiesFilter} from "@/composables/useEntitiesFilter";
import {ADMIN_USER_ID, displayUsersRelatedWithAdmin} from "@/helpers/util";
import { LOGOUT_STATUS } from "@/enum/extensionStatuses";
import {getInitialExtensionTime, getInitialTime} from "@/util/timeUtils";
import orderBy from "lodash/orderBy";
import {logOutStatus} from "@/enum/statusTypes";
import uniqBy from "lodash/uniqBy";
import cloneDeep from "lodash/cloneDeep";
import { useAutoAnimate } from '@formkit/auto-animate/vue'

const cardWidth = 600;
const baseWrapClass = 'redesign-base-card-container'
const extensionIdProp = 'number'
const parameterName = '{|extension_list|}'

const emit = defineEmits(['on-update']);

const props = defineProps({
    data: {
        type: Object,
        default: () => ({})
    }
});
/* Data */
const summaryOpened = ref({});
const showBodyAllCalls = ref({});
let loadedData = ref([])
const shadowColors = ref({})

const [ parent ] = useAutoAnimate({
    duration: 300
})

const sortBy = ref({
    label: i18n.t('extensions.sort.static'),
    value: 'number-asc',
    prop: extensionIdProp,
    order: 'asc'
})

const sortOptions = [
    {
        label: i18n.t('extensions.sort.created.asc'),
        value: 'number-asc',
        prop: extensionIdProp,
        order: 'asc'
    },
    {
        label: i18n.t('extensions.sort.created.desc'),
        value: 'number-desc',
        prop: extensionIdProp,
        order: 'desc'
    },
    {
        label: i18n.t('extensions.sort.userName.asc'),
        value: 'userName-asc',
        prop: 'userName',
        order: 'asc'
    },
    {
        label: i18n.t('extensions.sort.userName.desc'),
        value: 'userName-desc',
        prop: 'userName',
        order: 'desc'
    },
    {
        label: i18n.t('extensions.sort.statusTime.asc'),
        value: 'statusTime-asc',
        prop: 'statusTime',
        order: 'asc'
    },
    {
        label: i18n.t('extensions.sort.statusTime.desc'),
        value: 'statusTime-desc',
        prop: 'statusTime',
        order: 'desc'
    },
    {
        label: i18n.t('extensions.sort.callDuration.asc'),
        value: 'callDuration-asc',
        prop: 'callDuration',
        order: 'asc'
    },
    {
        label: i18n.t('extensions.sort.callDuration.desc'),
        value: 'callDuration-desc',
        prop: 'callDuration',
        order: 'desc'
    },
]
const localWidgetData = ref(cloneDeep(props.data))

/*Computed*/
const settings = computed(() => {
    return props.data.WidgetLayout.settings || realTimeSettings
})
const widgetConfig = computed(() => {
  return props.data.WidgetConfig || []
})
const { entitiesFilter } = useEntitiesFilter(widgetConfig , parameterName)

const layoutColumns = computed({
    get() {
        return get(localWidgetData.value.WidgetLayout, 'layoutColumns') || maxCardsToDisplay.value;
    },
    set(val) {
        localWidgetData.value.WidgetLayout.layoutColumns = val
        updateWidget()
    }
})
const columnsMode = computed({
    get () {
        return get(localWidgetData.value, 'WidgetLayout.settings.columnsMode', realTimeSettings.columnsMode)
    },
    set (val) {
        if (!localWidgetData.value.WidgetLayout.settings) {
            localWidgetData.value.WidgetLayout.settings = {}
        }
        localWidgetData.value.WidgetLayout.settings.columnsMode = val
        updateWidget()
    }
})
const layoutColumnsBasedOnColumnsMode = computed(() => {
    if (isStaticColumnsMode.value) {
        return layoutColumns.value
    } else {
        switch (columnsSize.value) {
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.EXTRA_SMALL:
                return 6
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.SMALL:
                return 5
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.MEDIUM:
                return 4
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.LARGE:
                return 3
            default:
                return 1
        }
    }
})
const columnsSize = computed({
    get () {
        return get(localWidgetData.value, 'WidgetLayout.settings.columnsSize', realTimeSettings.columnsSize)
    },
    set (val) {
        localWidgetData.value.WidgetLayout.settings.columnsSize = val
        updateWidget()
    }
})
const isStaticColumnsMode = computed(() => {
    return columnsMode.value === REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC
})
const extensions = computed(() => {
    return store.state.extensions.extensions;
});
const widgetSettings = computed(() => {
    return props.data.WidgetLayout.settings || realTimeSettings;
});

const renderColumns = computed(() => {
    if (!isStaticColumnsMode.value) {
        return {}
    }

    let columns = '';

    const columnsCount = sortedExtensions.value.length < layoutColumns.value
        ? sortedExtensions.value.length
        : layoutColumns.value

    times(columnsCount, () => {
        columns += ' auto';
    });
    return {
        'grid-template-columns': columns,
    };
});

const adminSelected = computed(() => {
  return displayUsersRelatedWithAdmin(props.data.WidgetConfig)
})
const filteredExtensions = computed(() => {
  if (adminSelected.value) {
    return uniqBy(mergedExtensions.value, extensionIdProp)
  }
  let currentExtensions = mergedExtensions.value.filter(ext => ext.userID !== ADMIN_USER_ID)
  currentExtensions = entitiesFilter(currentExtensions, extensionIdProp)
    return uniqBy(currentExtensions, extensionIdProp)

})
const usersData = computed(() => {
    if (loadedData.value.length) {
        return loadedData.value.reduce((accum, usersData) => {
            accum[usersData.user_id] = usersData
            return accum
        }, {})
    }
    return null
})
const onlineExtensions = computed(() => {
    let showLoggedOutUsers = get(props.data.WidgetLayout.settings, 'showLoggedOutUsers')
    if (!showLoggedOutUsers) {
        return extensions.value.filter(e => e.representativeStatus !== LOGOUT_STATUS)
    }
    return extensions.value
})
const mergedExtensions = computed(() => {
  if (usersData.value) {
    return onlineExtensions.value.map((item) => {
      const user = usersData.value[item.userID]
      if (user) {
        return  {...item, allDataSummary: user}
      }
      return item
    })
  }
  return onlineExtensions.value
})
const pageWidth = computed(() => {
    return store.getters['utils/pageWidth']
})
const maxCardsToDisplay = computed(() => {
    return parseInt(pageWidth.value / cardWidth - 1)
})
const sortedExtensions = computed(() => {
    const updatedExtensions = filteredExtensions.value.map(ext => {
        ext.statusTime = getInitialExtensionTime(ext, settings.value);

        if (ext.calls && ext.calls.length) {
            const earliestCall = ext.calls.reduce((a, b) =>
                a.callStarted_UTC_CLIENT < b.callStarted_UTC_CLIENT ? a : b
            );
            ext.callDuration = getInitialTime(earliestCall.callStarted_UTC_CLIENT);
        }

        return ext;
    });

    if (!sortBy.value) {
        return updatedExtensions;
    }

    if (sortBy.value.prop === 'callDuration') {
        const [withoutCalls, withCalls] = partition(updatedExtensions, ext => !ext.calls.length);

        const sortByCallStarted = (a, b) => {
            const aVal = getEarliestCallTime(a);
            const bVal = getEarliestCallTime(b);
            return compareValues(aVal, bVal, sortBy.value.order);
        };

        withCalls.sort(sortByCallStarted);

        const sortedWithoutCalls = orderBy(withoutCalls, 'lastAnsweredCallEventEpoch', 'asc');

        return sortBy.value.order === 'asc'
            ? withCalls.concat(sortedWithoutCalls)
            : sortedWithoutCalls.concat(withCalls);
    }
    const [unSortable, sortable] = partition(updatedExtensions, ext =>
        get(ext, sortBy.value.prop) === undefined || ext.representativeStatus === logOutStatus
    );

    const sorted = orderBy(sortable, sortBy.value.prop, sortBy.value.order);
    return sorted.concat(unSortable);
});
const sortedExtensionsDebounced = useThrottle(sortedExtensions, 500)

const cardWrapClasses = computed(() => {
    if (!layoutColumnsBasedOnColumnsMode.value || layoutColumnsBasedOnColumnsMode.value < 4) {
        return baseWrapClass
    }

    if (layoutColumnsBasedOnColumnsMode.value >= 6) {
        return `${baseWrapClass}-6`
    }

    return `${baseWrapClass}-${layoutColumnsBasedOnColumnsMode.value}`
})
/* Methods */
function updateWidget () {
    emit('on-update', localWidgetData.value)
}
const loadWidgetData = () => {
    getWidgetData(props.data).then(data => {
        if (data && Array.isArray(data)) {
            loadedData.value = data
        }
    })
}
const setSummaryVisibility = (cardKey, value) => {
    summaryOpened.value = {...summaryOpened.value, [cardKey]: value}
};
const compareValues = (a, b, order) => {
    return order === 'asc'
        ? (a > b
            ? 1
            : a < b
                ? -1
                : 0)
        : (a < b
            ? 1
            : a > b
                ? -1
                : 0)
}
const getEarliestCallTime = (extension) => {
    if (!extension.calls.length) {
        return null
    }

    if (extension.calls.length === 1) {
        return extension.calls[0].callStarted_UTC_CLIENT
    }

    return extension.calls.reduce(
        (acc, call) => {
            return acc.callStarted_UTC_CLIENT < call.callStarted_UTC_CLIENT
                ? acc.callStarted_UTC_CLIENT
                : call.callStarted_UTC_CLIENT
        }
    )
}
const updateGrid = (val) => {
    layoutColumns.value = val
}
const getSortingLabel = (option) => {
    const order = i18n.t(`sorting.${option.order}`)
    return `${option.label} - ${order}`
}
const changeShadowColor = (color, key) => {
    shadowColors.value = {...shadowColors.value, [key]: color}
}
const showAllCalls = (key, val) => {
    showBodyAllCalls.value = {...showBodyAllCalls.value, [key]: val}
};

/*watch*/
watch(() => sortBy.value, (newV) => {
    if (!newV) return;

    const { sortingParams } = props.data.WidgetLayout;

    if (sortingParams) {
        sortingParams.order = newV.order;
        sortingParams.prop = newV.prop;
    } else {
        localWidgetData.value.WidgetLayout.sortingParams = {
            order: newV.order,
            prop: newV.prop,
        };
    }
})

watch(
    () => props.data.WidgetLayout.sortingParams,
    (newV) => {
        if (newV && (sortBy.value.prop !== newV.prop || sortBy.value.order !== newV.order)) {
            sortBy.value = sortOptions.find(data =>
                data.order === newV.order &&
                (data.prop === newV.prop || data.prop === 'lastAnsweredCallEventEpoch')
            );
        }
    },
    { immediate: true }
)
watch(
    () => props.data,
    (newV) => {
        localWidgetData.value = cloneDeep(newV)
    },
    {
        deep: true
    }
)

loadWidgetData()
</script>



<style scoped lang="scss">
.square {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 2px solid var(--silver-color);

    &:hover {
        background: var(--silver-color);
        border: 2px solid var(--primary-color);
    }
}

.mx-margin--1 {
    margin-right: 1px;
    margin-left: 1px;
}

.redesign-extension-cards-wrap {
    height: fit-content;

    &.columns-auto {
        @apply flex justify-center;

        .redesign-extension-cards {
            @apply inline-flex flex-wrap justify-center;
        }
    }

    .redesign-extension-cards {
        @apply grid w-max mx-auto;
    }
}
.redesign-base-card-container {
    max-height: 230px;
}
.redesign-base-card-container-3, .redesign-base-card-container {
    width: 600px;
}
.redesign-base-card-container-4 {
    width: 446px;
}
.redesign-base-card-container-5 {
    width: 354px;
}
.redesign-base-card-container-6 {
    width: 292px;
}
</style>
